import { useState, useCallback, useRef, useEffect } from 'react';
import { X, Upload, ZoomIn, RotateCw } from 'lucide-react';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../../lib/canvasUtils';
import { DEFAULT_COLORS } from '../../lib/icons';
import { processImageFile } from '../../lib/imageUtils';
import { useTranslations } from '../../i18n';
import { AlertModal } from '../ui/AlertModal';

type CarIconPickerProps = {
    isOpen: boolean;
    onClose: () => void;
    onSave: (result: {
        type: 'emoji' | 'image';
        value: string; // Emoji char or Image URL
        color?: string;
        hue?: number;
        file?: File; // If image, raw file for upload
        thumbFile?: File; // <--- The resized thumbnail
    }) => void;
    initialColor: string;
    initialEmoji: string;
    initialImage?: string;
};

const EMOJI_PRESETS = [
    '🚗', '🚘', '🚙', '🏎️', '🚕', '🚓', '🚑', '🚒', '🚐', '🚚', '🚛', '🚜', '🛵', '🏍️', '🚲', '🛴',
    '🚃', '🚄', '🚅', '🚆', '🚇', '🚈', '🚉', '🚊', '🚝', '🚞', '🚋', '🚌', '🚍', '🚎',
];

const generateThumbnail = async (blob: Blob, maxWidth: number, maxHeight: number): Promise<File | undefined> => {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            let { width, height } = img;
            if (width > maxWidth) {
                height = Math.round((height * maxWidth) / width);
                width = maxWidth;
            }
            if (height > maxHeight) {
                width = Math.round((width * maxHeight) / height);
                height = maxHeight;
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, width, height);
            canvas.toBlob((b) => {
                if (b) {
                    resolve(new File([b], "car_icon_thumb.jpg", { type: "image/jpeg" }));
                } else {
                    resolve(undefined);
                }
            }, 'image/jpeg', 0.85);
        };
        img.onerror = () => resolve(undefined);
        img.src = URL.createObjectURL(blob);
    });
};

export function CarIconPicker({ isOpen, onClose, onSave, initialColor, initialEmoji, initialImage }: CarIconPickerProps) {
    const { t } = useTranslations();
    const [activeTab, setActiveTab] = useState<'color' | 'image'>('color');
    const [selectedColor, setSelectedColor] = useState(initialColor);
    const [selectedEmoji, setSelectedEmoji] = useState(initialEmoji);
    const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });

    // Image State
    const [imageSrc, setImageSrc] = useState<string | null>(initialImage || null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            setSelectedColor(initialColor);
            setSelectedEmoji(initialEmoji);
            setImageSrc(initialImage || null);
            setActiveTab(initialImage ? 'image' : 'color');
        }
    }, [isOpen, initialColor, initialEmoji, initialImage]);

    const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    if (!isOpen) return null;

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            let file = e.target.files[0];
            file = await processImageFile(file);
            console.log("File selected:", file.name, file.type, file.size);

            const reader = new FileReader();
            reader.onload = () => {
                if (reader.result) {
                    console.log("File loaded successfully");
                    setImageSrc(reader.result.toString());
                    setActiveTab('image');
                } else {
                    console.error("File reader result is empty");
                    setAlertConfig({ isOpen: true, title: t.common.error, message: 'Failed to read file.' });
                }
            };
            reader.onerror = (err) => {
                console.error("File reading failed:", err);
                setAlertConfig({ isOpen: true, title: t.common.error, message: 'Error reading file.' });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleConfirmImage = async () => {
        console.log("Confirming image...", { hasImageSrc: !!imageSrc, hasPixels: !!croppedAreaPixels });
        if (!imageSrc || !croppedAreaPixels) {
            console.warn("Validation failed: missing image or crop data");
            return;
        }

        try {
            console.log("Starting cropping...");
            const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
            console.log("Crop result:", croppedBlob);

            if (croppedBlob) {
                // Create a file from blob
                const file = new File([croppedBlob], "car_icon.jpg", { type: "image/jpeg" });
                const thumbFile = await generateThumbnail(croppedBlob, 1200, 900);
                const previewUrl = URL.createObjectURL(croppedBlob);

                console.log("Saving image:", { previewUrl, fileSize: file.size });
                onSave({ type: 'image', value: previewUrl, file, thumbFile });
                onClose();
            } else {
                console.error("Cropped blob is null");
                setAlertConfig({ isOpen: true, title: t.common.error, message: 'Failed to crop image.' });
            }
        } catch (e) {
            console.error("Error cropping image:", e);
            setAlertConfig({ isOpen: true, title: t.common.error, message: 'An error occurred while processing the image.' });
        }
    };

    const handleConfirmColor = () => {
        onSave({ type: 'emoji', value: selectedEmoji, color: selectedColor });
        onClose();
    };

    return (
        <>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

                <div className="relative bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
                        <h3 className="font-bold text-lg text-gray-900">{t.cars.chooseIcon}</h3>
                        <button onClick={onClose} className="p-2 hover:bg-gray-50 rounded-full transition-colors">
                            <X size={20} className="text-gray-400" />
                        </button>
                    </div>

                    <div className="flex p-2 gap-2 border-b border-gray-100">
                        <button
                            onClick={() => setActiveTab('image')}
                            className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all ${activeTab === 'image' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:bg-gray-50'
                                }`}
                        >
                            {t.cars.imageTab}
                        </button>
                        <button
                            onClick={() => setActiveTab('color')}
                            className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all ${activeTab === 'color' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:bg-gray-50'
                                }`}
                        >
                            {t.cars.iconTab}
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 space-y-8">
                        {activeTab === 'color' ? (
                            <>
                                {/* Preview */}
                                <div className="flex justify-center">
                                    <div
                                        className="w-32 h-24 rounded-[1.5rem] flex items-center justify-center shadow-inner transition-all duration-300"
                                        style={{
                                            background: selectedColor
                                        }}
                                    >
                                        <span className="text-6xl">{selectedEmoji}</span>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">
                                        {t.cars.chooseColor}
                                    </label>
                                    <div className="flex flex-wrap justify-center gap-3">
                                        {DEFAULT_COLORS.map(c => (
                                            <button
                                                key={c}
                                                onClick={() => setSelectedColor(c)}
                                                className={`w-10 h-10 rounded-full transition-transform ${selectedColor === c ? 'scale-125 ring-2 ring-offset-2 ring-gray-200' : 'hover:scale-105'}`}
                                                style={{ background: c }}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">
                                        {t.cars.chooseEmoji}
                                    </label>
                                    <div className="grid grid-cols-6 gap-2">
                                        {EMOJI_PRESETS.map(emoji => (
                                            <button
                                                key={emoji}
                                                onClick={() => setSelectedEmoji(emoji)}
                                                className={`aspect-square flex items-center justify-center text-2xl rounded-xl transition-colors ${selectedEmoji === emoji ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'}`}
                                            >
                                                {emoji}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="space-y-6 h-full flex flex-col">
                                {!imageSrc ? (
                                    <div
                                        onClick={() => fileInputRef.current?.click()}
                                        className="flex-1 border-2 border-dashed border-gray-200 rounded-3xl flex flex-col items-center justify-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors min-h-[300px]"
                                    >
                                        <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center">
                                            <Upload className="text-blue-600" size={32} />
                                        </div>
                                        <div className="text-center">
                                            <p className="font-bold text-gray-900">{t.cars.uploadImage}</p>
                                            <p className="text-sm text-gray-400">{t.cars.clickToBrowse}</p>
                                        </div>
                                    </div>
                                ) : imageSrc === initialImage ? (
                                    <div className="flex-1 flex flex-col h-full min-h-[400px] items-center justify-center space-y-6">
                                        <div className="w-48 h-36 rounded-[2rem] overflow-hidden shadow-xl ring-4 ring-gray-100 flex-shrink-0">
                                            <img src={imageSrc} alt="Current Icon" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="text-center space-y-3">
                                            <p className="text-gray-500 font-medium text-sm">{t.cars.currentCarIcon}</p>
                                            <button
                                                onClick={() => fileInputRef.current?.click()}
                                                className="px-6 py-3 bg-blue-50 text-blue-600 rounded-xl font-bold text-sm uppercase tracking-wider hover:bg-blue-100 transition-colors"
                                            >
                                                {t.cars.changeImage}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex-1 flex flex-col h-full min-h-[400px]">
                                        <div className="relative w-full h-64 bg-gray-900 rounded-2xl overflow-hidden mb-4 ring-4 ring-gray-100">
                                            <Cropper
                                                image={imageSrc}
                                                crop={crop}
                                                zoom={zoom}
                                                rotation={rotation}
                                                aspect={4 / 3}
                                                onCropChange={setCrop}
                                                onCropComplete={onCropComplete}
                                                onZoomChange={setZoom}
                                                onRotationChange={setRotation}
                                                cropShape="rect"
                                                showGrid={false}
                                                objectFit="contain"
                                                classes={{
                                                    containerClassName: "rounded-2xl"
                                                }}
                                                style={{
                                                    containerStyle: { borderRadius: '1rem' },
                                                    mediaStyle: {},
                                                    cropAreaStyle: {
                                                        borderRadius: '24px',
                                                        border: '2px solid white',
                                                        boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)'
                                                    }
                                                }}
                                            />
                                        </div>

                                        <div className="space-y-4 px-2">
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-xs font-bold text-gray-500 uppercase">
                                                    <span>{t.cars.zoom}</span>
                                                    <ZoomIn size={14} />
                                                </div>
                                                <input
                                                    type="range"
                                                    value={zoom}
                                                    min={1}
                                                    max={3}
                                                    step={0.1}
                                                    aria-labelledby="Zoom"
                                                    onChange={(e) => setZoom(Number(e.target.value))}
                                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-xs font-bold text-gray-500 uppercase">
                                                    <span>{t.cars.rotate}</span>
                                                    <RotateCw size={14} />
                                                </div>
                                                <input
                                                    type="range"
                                                    value={rotation}
                                                    min={0}
                                                    max={360}
                                                    step={1}
                                                    aria-labelledby="Rotation"
                                                    onChange={(e) => setRotation(Number(e.target.value))}
                                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                                />
                                            </div>
                                            <button
                                                onClick={() => setImageSrc(null)}
                                                className="w-full py-3 text-sm font-bold text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                                            >
                                                {t.cars.removeChangeImage}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                            </div>
                        )}
                    </div>

                    <div className="p-6 border-t border-gray-100 bg-white">
                        <button
                            onClick={activeTab === 'color' ? handleConfirmColor : handleConfirmImage}
                            disabled={activeTab === 'image' && !imageSrc}
                            className="w-full bg-blue-600 text-white py-4 rounded-2xl font-bold text-lg hover:bg-blue-700 active:scale-[0.98] transition-all shadow-lg shadow-blue-200 disabled:opacity-50 disabled:shadow-none"
                        >
                            {activeTab === 'color' ? t.cars.selectIcon : t.cars.cropAndSave}
                        </button>
                    </div>
                </div>
            </div>
            <AlertModal
                isOpen={alertConfig.isOpen}
                title={alertConfig.title}
                message={alertConfig.message}
                confirmLabel={t.common.ok}
                onClose={() => setAlertConfig({ isOpen: false, title: '', message: '' })}
            />
        </>
    );
}
